import { API } from '@/state/api';
import streamableFidsFilter from '@/utils/streamableFids';
import { change as ChangeRulesPack } from '@custom/dataGrid/classRules';
import {
  AnalystsRenderer,
  CandleRenderer,
  DataQualityRenderer,
  DefaultRenderer,
  FlagRenderer,
  JSQuoteRenderer,
  JSQuoteTickerRenderer,
  JSTickFlashRenderer,
  PerformanceRenderer,
  RangeRenderer,
  SectorRenderer,
  TrendArrowRenderer,
  TrendChartRenderer
} from '@custom/dataGrid/renderers';
import CWidgetInputSwitch from '@custom/widgetContainer/CWidgetInputSwitch';
import gridNavigation from '@mixins/gridNavigation';
import widgetCommons from '@mixins/widgetCommons';
import styleVars from '@mm5-components/scss/variables.scss';
import { fromSymbol } from '@state/api';
import orderUtils from '@widgets/WOrderEntryWidget/orderUtils.js';
import moment from 'moment';
import resize from 'vue-resize-directive';

export default {
  directives: {
    resize
  },
  mixins: [widgetCommons, gridNavigation, orderUtils],
  components: {
    AnalystsRenderer,
    CandleRenderer,
    CWidgetInputSwitch,
    DataQualityRenderer,
    FlagRenderer,
    PerformanceRenderer,
    RangeRenderer,
    SectorRenderer,
    TrendArrowRenderer,
    TrendChartRenderer,
    DefaultRenderer,
    JSTickFlashRenderer,
    JSQuoteRenderer,
    JSQuoteTickerRenderer
  },
  data() {
    return {
      defaultColumnDef: {
        headerClass: 'text-center',
        cellClass: 'text-right',
        comparator: this.sortComparator,
        minwidth: 0,
        width: 60,
        resizable: true,
        sortable: true,
        suppressKeyboardEvent: this.suppressKeyboardEvent,
        suppressMenu: true,
        valueFormatter: this.fidletValueFormatter,
        cellRenderer: 'DefaultRenderer',
        cellRendererParams: () => {
          return { state: this.$store.state };
        }
      },
      disableTableFidsUpdate: false,
      excludedCsvExportFids: [
        'calc.52_WEEKS_RANGE',
        'calc.QUOTE_TICKER',
        'calc.REL_RANGE',
        'calc.TREND',
        'calc.TREND_ARROW'
      ],
      fidColumnDefs: {
        action: {
          cellClass: ['function-btn-cell', 'actionable-cell'],
          // cellRenderer: undefined, // required to be implemented in the consuming widget
          maxWidth: 30,
          width: 20,
          pinned: true,
          lockPosition: true,
          sortable: false,
          type: ['actionable']
        },
        'calc.52_WEEKS_RANGE': {
          resizable: true,
          cellClass: 'text-center',
          width: 200,
          maxWidth: 200,
          valueFormatter: params => {
            return {
              ...params.value,
              startFormatted: params.value ? this.$f(params.value.start, 'float') : '-',
              endFormatted: params.value ? this.$f(params.value.end, 'float') : '-'
            };
          },
          cellRenderer: 'RangeRenderer',
          comparator(valA, valB) {
            return (
              (valA.mark - valA.start) / (valA.end - valA.start) - (valB.mark - valB.start) / (valB.end - valB.start)
            );
          }
        },
        'calc.MARK': {
          cellRenderer: JSTickFlashRenderer,
          valueGetter: params => {
            if (!params.data) return '';
            const data = params.data['calc.MARK'];
            if (!data || isNaN(data.bid) || isNaN(data.ask)) return '-';
            return this.$f((Number(data.bid) + Number(data.ask)) / 2, 'float');
          }
        },
        'calc.PERFORMANCE': {
          cellRenderer: 'PerformanceRenderer',
          width: 100,
          maxWidth: 275,
          valueFormatter: params => {
            if (!params.data) return '';
            return params.data['calc.PERFORMANCE'] ? this.$f(params.data['calc.PERFORMANCE'], 'float') : '-';
          }
        },
        'calc.QUOTE_TICKER': {
          sortable: false,
          resizable: true,
          cellClass: 'text-left',
          cellRenderer: JSQuoteTickerRenderer,
          width: 80
        },
        'calc.RANGE': {
          valueGetter: params => {
            if (!params.data) return '';
            const data = params.data['calc.RANGE'];
            if (isNaN(data?.high) || isNaN(data?.low)) return '-';
            return this.$f(Number(data?.high) + Number(data?.low), 'float');
          }
        },
        'calc.REL_RANGE': {
          sortable: false,
          cellRenderer: 'CandleRenderer',
          width: 80
        },
        'calc.TREND': {
          sortable: false,
          resizable: true,
          cellClass: 'text-left',
          cellRenderer: 'TrendChartRenderer',
          width: 70,
          maxWidth: 70
        },
        'calc.TREND_ARROW': {
          cellClassRules: ChangeRulesPack,
          cellRenderer: 'TrendArrowRenderer',
          resizable: true,
          width: 30
        },
        'calc.ITM_OTM': {
          resizable: true,
          width: 70,
          valueGetter: params => {
            if (!params.data || !params.data['calc.ITM_OTM']) return '';

            const strikePrice = Number(params.data['q.STRIKE_PRC']);
            const underlyingTradePrice = Number(params.data['calc.ITM_OTM']['underlyingTradePrice']);

            if (isNaN(strikePrice) || isNaN(underlyingTradePrice)) return '';

            const optionType = this.optionTypeCache[params.data['q.RIC']];
            const itmText = this.$t('white-label.renderers.itmOtm.itm'),
              otmText = this.$t('white-label.renderers.itmOtm.otm');
            if (strikePrice < underlyingTradePrice) return optionType === 'C' ? itmText : otmText;
            else if (strikePrice > underlyingTradePrice) return optionType === 'C' ? otmText : itmText;
            else return this.$t('white-label.renderers.itmOtm.atm');
          }
        },
        'earn.COMBI_DATE': {
          cellClass: 'text-left',
          width: 75
        },
        'earn.EST_MEAN': {
          width: 75
        },
        'earn.EST_NUM': {
          cellClass: 'text-right',
          width: 75
        },
        'earn.ACTUAL': {
          width: 80
        },
        'earn.MARKET_PHASE': {
          width: 100
        },
        'earn.PERIOD_NAME': {
          width: 90
        },
        'earn.PREV_YEAR_ACTUAL': {
          width: 80
        },
        'earn.PREV_QUARTER_ACTUAL': {
          width: 90
        },
        'q._ASK': {
          cellRenderer: JSQuoteRenderer,
          cellClass: ['text-right'],
          tooltipValueGetter: this.tooltipValueGetter('q._ASK'),
          type: ['actionable']
        },
        'q.ASKSIZE': {
          cellRenderer: JSTickFlashRenderer
        },
        'q._BID': {
          cellRenderer: JSQuoteRenderer,
          cellClass: ['text-right'],
          tooltipValueGetter: this.tooltipValueGetter('q._BID'),
          type: ['actionable']
        },
        'q.BIDSIZE': {
          valueFormatter: params => {
            let fid = params.colDef.field;
            var formatedBidSize = this.$f(params.value, this.formats[fid]);
            return formatedBidSize == '+0' || formatedBidSize == '-0' ? '0' : formatedBidSize;
          },
          cellRenderer: JSTickFlashRenderer
        },
        'q._COUNTRY': {
          resizable: true,
          cellClass: 'text-center',
          cellRenderer: 'FlagRenderer',
          width: 37,
          maxWidth: 37
        },
        'q._CURRENCY': {
          width: 35
        },
        'q._DATA_QUALITY': {
          cellClass: 'text-center',
          cellRenderer: 'DataQualityRenderer',
          width: 35,
          tooltipValueGetter: params =>
            params.value == 'R' ? this.i18n('realtimeData', 'globalLabels') : this.i18n('delayedData', 'globalLabels')
        },
        'q._DSPLY_NAME': {
          resizable: true,
          cellClass: 'text-left',
          width: 110
        },
        'q._NETCHNG_1': {
          cellClassRules: ChangeRulesPack,
          valueFormatter: params => {
            if (params.value) {
              if (Number(params.value) === 0.0) return this.$f(parseFloat(params.value), 'float.price');
              return this.$f(params.value, 'float.price.change');
            } else {
              return '-';
            }
          },
          width: 55
        },
        'q._PCTCHNG': {
          cellClassRules: ChangeRulesPack,
          width: this.isSidebar ? 50 : 55,
          valueFormatter: params => {
            let fid = params.colDef.field;
            if (this.isSidebar) return this.$f(params.value, this.formats[fid]).replace(' ', '');
            else {
              if (Number(params.value) === 0.0) return this.$f(parseFloat(params.value), 'float.percentage');
              return this.$f(params.value, this.formats[fid]);
            }
          }
        },
        'q.RIC': {
          cellClass: 'text-left text-primary',
          width: 80
        },
        'q._TCR': {
          cellClass: ['text-left'],
          cellClassRules: {
            'not-available': params => params.data && !!params.data.NA,
            negative: params => params.data && params.data['q._NETCHNG_1'] < 0,
            positive: params => params.data && params.data['q._NETCHNG_1'] > 0,
            'text-primary': params => params.data && params.data['q._NETCHNG_1'] === 0
          },
          width: 80,
          cellStyle: params => {
            if (
              params.data &&
              params.data['q.TRD_STATUS'] &&
              this.isValidTradingHaltValue(params.data['q.TRD_STATUS'])
            ) {
              return { 'border-style': 'solid', 'border-width': '1px', 'border-color': 'var(--red)' };
            } else return { border: 'none' };
          },
          tooltipValueGetter: params => {
            if (params.data) {
              if (params.data['q.TRD_STATUS'] && this.isValidTradingHaltValue(params.data['q.TRD_STATUS'])) {
                return `${params.data['q._DSPLY_NAME']} ( ${this.i18n(
                  params.data['q.TRD_STATUS'].trim(),
                  'globalLabels.tradingHalt'
                )} )`;
              } else {
                return params.data['q._DSPLY_NAME'];
              }
            } else return '';
          },
          type: ['actionable']
        },
        'q._TRDPRC_1': {
          cellClass: ['text-right'],
          cellClassRules: {
            negative: params => params.data && params.data['q._NETCHNG_1'] < 0,
            positive: params => params.data && params.data['q._NETCHNG_1'] > 0,
            neutral: params => params.data && params.data['q._NETCHNG_1'] === 0
          },
          cellRenderer: JSTickFlashRenderer,
          width: this.isSidebar ? 45 : 60
        },
        'rkd.COMP_TAXONOMY_TRBC_CD_L1': {
          resizable: true,
          cellClass: 'text-center',
          width: 35,
          valueFormatter: params => {
            return params.value;
          },
          cellRenderer: 'SectorRenderer'
        },
        'rkd.RECOM_MEAN_CURR': {
          cellClass: 'text-center',
          width: 90,
          cellRenderer: 'AnalystsRenderer',
          tooltipValueGetter: params => {
            if (params.data) {
              let mean = params.data['rkd.RECOM_MEAN_CURR'];
              if (mean < 1.5) return `${this.$t('white-label.widgets.analystEvaluation.labels.strong-buy')} ${mean}`;
              if (mean >= 1.5 && mean < 2.5)
                return `${this.$t('white-label.widgets.analystEvaluation.labels.buy')} ${mean}`;
              if (mean >= 2.5 && mean < 3.5)
                return `${this.$t('white-label.widgets.analystEvaluation.labels.hold')} ${mean}`;
              if (mean >= 3.5 && mean < 4.5)
                return `${this.$t('white-label.widgets.analystEvaluation.labels.sell')} ${mean}`;
              if (mean >= 4.5) return `${this.$t('white-label.widgets.analystEvaluation.labels.strong-sell')} ${mean}`;
            }
          },
          valueGetter: params => {
            return params.data ? Number(params.data['rkd.RECOM_MEAN_CURR']) : '';
          }
        },
        'rkd.MKTCAP_MOREY': {
          valueFormatter: params => {
            if (!params.data) return '';
            return params.data['rkd.MKTCAP_MOREY'] ? this.$f(params.data['rkd.MKTCAP_MOREY'], 'float') + ' Mn' : '-';
          },
          width: 90
        },
        'stoscr.NUM_MOVES(0)': {
          width: 75
        },
        'stoscr.VOLUME(0)': {
          width: 80
        },
        'x._TICKER': {
          cellClass: 'text-primary',
          width: 50
        },
        'd._AVGVOL_6M': {
          valueFormatter: params => this.$f(Math.round(params.value), 'int')
        },
        'q._YIELD': {
          valueFormatter: params => {
            if (!params.node.group && Number(params.value) === 0.0) params.value = 0.0;
            return this.$f(params.value, 'float.percentage');
          }
        },
        'q.DIVIDEND': {
          valueFormatter: params => {
            if (!params.node.group && Number(params.value) === 0.0) params.value = 0.0;
            return this.$f(params.value, 'float.price');
          }
        },
        'q._TRDVOL_1': {
          valueFormatter: params => {
            if (!params.node.group && Number(params.value) === 0.0) params.value = 0.0;
            return this.$f(params.value, 'int');
          }
        },
        'q.PERATIO': {
          valueFormatter: params => {
            if (!params.node.group && Number(params.value) === 0.0) params.value = 0.0;
            return this.$f(params.value, 'float');
          }
        }
      },
      formats: {
        'rkd.SHARES_ISSUED_MOREY': 'int' // Because there is no metadata available for SHARES_ISSUED_MOREY
      },
      lastColMoveEvent: null,
      netChanges: {},
      percentChanges: {},
      performanceBar: {
        totalAdvances: 0,
        totalDeclines: 0,
        totalNeutral: 0,
        advances: 0,
        declines: 0
      },
      selectedTemplateConfig: null,
      subscriptionIds: [],
      tableFids: [],
      tableFieldsAppend: [],
      tableFieldsPrepend: [],
      isShiftTabPressed: false,
      isTabPressed: false,
      optionTypeCache: {},
      underlyingStreamingId: null,
      underlyingRicsMap: {}
    };
  },
  computed: {
    agGridRowHeight() {
      return parseInt(styleVars['rowHeight' + this.fontSize.toUpperCase()]);
    },
    applicationLanguage() {
      return this.applicationSettings.applicationLanguage;
    },
    applicationSettings() {
      return this.$store.state.applicationSettings.general.settings;
    },
    isCellAnimationDisabled() {
      return this.applicationSettings.flashDisplay.flashMode === 'noanimation';
    },
    autosortOptions() {
      return [
        {
          value: true,
          text: this.i18n('on', 'globalLabels')
        },
        {
          value: false,
          text: this.i18n('off', 'globalLabels')
        }
      ];
    },
    columnDefs() {
      return this.tableFids.map(fid => {
        const cfg = {
          colId: fid,
          field: fid,
          headerName: this.i18n(fid + '.short', 'globalLabels.fids'),
          headerTooltip: this.i18n(fid + '.full', 'globalLabels.fids'),
          pinned: this.relatedFieldsConfig.pinnedFids.includes(fid),
          ...(this.fidColumnDefs[fid] || {}),
          ...((this.extendedColDefs && this.extendedColDefs[fid]) || {})
        };
        cfg.lockPosition = cfg.pinned;
        cfg.pinned = cfg.pinned && this.pinColumnsAllowed;
        return cfg;
      });
    },
    //This needs to be removed
    columnState: {
      get() {
        return this.widgetStore.columnState;
      },
      set(val) {
        this.$store.commit(`widget/${this.wid}/COLUMN_STATE_CHANGED`, val);
      }
    },
    templateColumnState: {
      get() {
        const templateName = this.widgetStore.template;
        if (templateName) {
          const template = this.relatedFieldsConfig.templates.find(template => template.name === templateName);
          return template.columnState;
        }
        return [];
      },
      set(columnState) {
        this.$store.dispatch('fieldsConfig/setTemplateColumnState', {
          widget: this.factoryName,
          name: this.controlFieldsConfigName,
          columnState
        });
      }
    },
    commaSeparator() {
      return this.applicationSettings.numberFormat.commaSeparator;
    },
    pinColumnsAllowed() {
      return !this.$store.state.applicationSettings.accessibility.settings.accessibilityFeatures;
    },
    controlAutosort: {
      get() {
        return this.widgetStore.autosort;
      },
      set(val) {
        this.$store.commit(`widget/${this.wid}/AUTOSORT_CHANGED`, val);
      }
    },
    controlFieldsConfigName: {
      get() {
        const nameInStore = this.widgetStore.template;
        if (nameInStore && this.relatedTemplates.find(template => template.name === nameInStore)) return nameInStore;
        else {
          if (this.relatedFieldsConfig && this.relatedFieldsConfig.defaultTemplate)
            return this.relatedTemplates.find(template => template.name === this.relatedFieldsConfig.defaultTemplate)
              ? this.relatedFieldsConfig.defaultTemplate //Default template exist
              : this.relatedTemplates && this.relatedTemplates.length
              ? this.relatedTemplates[0].name //Else return the first template in the list
              : '';
          return '';
        }
      },
      set(name) {
        // don't set the template if it's already set from an external component
        if (name === this.controlFieldsConfigName) return;
        this.$store.commit(`widget/${this.wid}/TEMPLATE_SET`, name);
      }
    },
    controlSecuritiesType() {
      return this.widgetStore.securitiesType;
    },
    fieldsConfigNameOptions() {
      return this.relatedTemplates.map(template => ({
        value: template.name,
        text: template.custom ? template.name : this.$t('white-label.templates.' + template.name)
      }));
    },
    gridUpdateInterval() {
      return this.applicationSettings.quoteSpeed;
    },
    percentChangeMin() {
      return Object.values(this.percentChanges).reduce((acc, val) => {
        acc = Math.min(acc, val);
        return acc;
      }, 0);
    },
    percentChangeMax() {
      return Object.values(this.percentChanges).reduce((acc, val) => {
        acc = Math.max(acc, val);
        return acc;
      }, 0);
    },
    // enable to support widget synchronization during delayed updates
    //reflushFlag() {
    //  return this.$store.state.widget.reflushFlag;
    //},
    relatedFieldsConfig() {
      return this.$store.state.fieldsConfig.widgets[this.factoryName];
    },
    relatedTemplates() {
      if (this.isSidebar && this.relatedFieldsConfig.sidebarTemplates) return this.relatedFieldsConfig.sidebarTemplates;
      return this.relatedFieldsConfig.templates;
    },
    requestFids() {
      const fids = this.$store.getters['fieldsConfig/endpointFids'](this.factoryName, this.controlFieldsConfigName);
      if (typeof this.showPerformanceBar === 'boolean' && !fids.includes('q._NETCHNG_1')) fids.push('q._NETCHNG_1');
      return fids;
    },
    securitiesTypeOptions() {
      return [
        {
          value: 'stocks',
          text: this.i18n('stocks', 'globalLabels')
        },
        {
          value: 'options',
          text: this.i18n('options', 'globalLabels')
        }
      ];
    },
    showPerformanceBar() {
      return this.widgetStore.performanceBar;
    },
    _tableFids() {
      if (!this.selectedTemplateConfig) return [];
      return this.tableFieldsPrepend.concat(
        this.relatedFieldsConfig.pinnedFids,
        this.relatedFieldsConfig.requiredFids.filter(
          fid => !this.relatedFieldsConfig.pinnedFids.includes(fid) && !this.selectedTemplateConfig.fids.includes(fid)
        ),
        this.selectedTemplateConfig.fids.filter(fid => !this.relatedFieldsConfig.pinnedFids.includes(fid)),
        this.tableFieldsAppend
      );
    },
    twoDecimalPlaces() {
      return this.applicationSettings.numberFormat.twoDecimalPlaces;
    },
    stocksBuyAtBid() {
      return this.$store.state.applicationSettings.orders.settings.stocks.buySellAtType === 'buyAtBid';
    },
    optionsBuyAtBid() {
      return this.$store.state.applicationSettings.orders.settings.options.buySellAtType === 'buyAtBid';
    }
  },
  methods: {
    //This method needs to be called instead of the default this.gridOptions.api.setColumnDefs when we want to maintain the column order in grid.
    setColumnDefs(cols = this.columnDefs) {
      this.gridOptions.maintainColumnOrder = true;
      this.gridOptions.api.setColumnDefs(cols);
      this.gridOptions.maintainColumnOrder = false;
    },
    autoSizeColumns(colIdList = null) {
      if (this.gridOptions && this.gridOptions.columnApi && colIdList === null) {
        this.gridOptions.columnApi.autoSizeAllColumns(false);
        this.saveCurrentColumnState(this.gridOptions.columnApi);
      } else if (this.gridOptions && this.gridOptions.columnApi && colIdList) {
        this.gridOptions.columnApi.autoSizeColumns(colIdList, false);
        this.saveCurrentColumnState(this.gridOptions.columnApi);
      }
    },
    fidletValueFormatter(params) {
      let fid = params.colDef.field;
      if ((this.fidColumnDefs[fid] || (this.extendedColDefs && this.extendedColDefs[fid])) && params.node.group) {
        if (!this._.isNumber(params.value)) return '';
      }

      //this was a part of valueGetter before in a method called rawTypeValueGetter
      let rawFidType = this.formats[fid] ? this.formats[fid].split('.')[0] : 'undefined';
      let value = params.value;

      if (rawFidType === 'int') {
        value = parseInt(value);
      } else if (rawFidType === 'float') {
        value = parseFloat(value);
      }
      //EOR

      var formattedValue = this.$f(value, this.formats[fid]);

      if (params.column.colId === 'q.DIVPAYDATE' || params.column.colId === 'q.EXDIVDATE') {
        if (
          formattedValue === 'Invalid Date' ||
          formattedValue === '-' ||
          (params.data && params.data['q._TYPE'] === 'OPT')
        ) {
          formattedValue = this.$t('white-label.globalLabels.n/a');
        }
      } else if (params.column.colId === 'q.EXPIR_DATE') {
        if (formattedValue.trim() === '') formattedValue = '-';
      }

      return formattedValue;
    },
    getSortState(columnApi = this.gridOptions.columnApi) {
      return columnApi.getColumnState().reduce((acc, col) => {
        if (col.sort) {
          acc.push({
            colId: col.colId,
            sort: col.sort,
            sortIndex: col.sortIndex
          });
        }
        return acc;
      }, []);
    },
    onColMoved(ev) {
      this.lastColMoveEvent = ev.toIndex !== undefined ? ev : null;
    },
    onColResized(ev) {
      if ((ev.source !== 'uiColumnDragged' && ev.source !== 'autosizeColumns') || !ev.finished) return;
      this.saveCurrentColumnState(ev.columnApi);
    },
    onGridDragEnd() {
      if (!this.lastColMoveEvent) return;
      let templateName = this.controlFieldsConfigName;
      const origTemplate = this.relatedTemplates.find(t => t.name === templateName);
      if (!origTemplate.custom) {
        templateName = this.cloneCurrentTemplate();
      }
      const newTemplate = this.relatedTemplates.find(t => t.name === templateName);
      let prevSiblingFid = this.lastColMoveEvent.columnApi.getColumnState()[this.lastColMoveEvent.toIndex - 1].colId;
      // accomodate for colId naming in holdings widget etc
      if (!prevSiblingFid.includes('.')) {
        prevSiblingFid = 'td.' + prevSiblingFid;
      }
      const movingColFid = this.lastColMoveEvent.column.colId.includes('.')
        ? this.lastColMoveEvent.column.colId
        : 'td.' + this.lastColMoveEvent.column.colId;
      const movingColPos = newTemplate.fids.indexOf(movingColFid);
      const prevSiblingPos = newTemplate.fids.indexOf(prevSiblingFid);
      this.disableTableFidsUpdate = true;
      this.$store.dispatch('fieldsConfig/shiftFidsToPos', {
        widget: this.factoryName,
        name: templateName,
        fid: movingColFid,
        newIndex: prevSiblingPos + (prevSiblingPos < movingColPos ? 1 : 0)
      });
      if (!origTemplate.custom) {
        this.controlFieldsConfigName = templateName;
      }
      this.lastColMoveEvent = null;
    },
    saveCurrentColumnState(columnApi) {
      const colState = columnApi.getColumnState().map(e => ({
        colId: e.colId,
        flex: e.flex,
        width: e.width
      }));

      if (!this.isSidebar) {
        //If standard template, then clone a new template
        const origTemplate = this.relatedTemplates.find(t => t.name === this.controlFieldsConfigName);
        if (!origTemplate.custom) {
          const newName = this.cloneCurrentTemplate();
          this.controlFieldsConfigName = newName;
        }

        this.templateColumnState = colState;
      } else {
        this.columnState = colState;
      }
    },
    cloneCurrentTemplate() {
      const templateName = this.controlFieldsConfigName;
      let newName = templateName + ' Edit';
      if (this.relatedTemplates.find(t => t.name === newName)) {
        for (let i = 1; ; i++) {
          const loopName = newName + ' ' + i;
          if (!this.relatedTemplates.find(t => t.name === loopName)) {
            newName = loopName;
            break;
          }
        }
      }
      // direct mutation, as we don't want to persist fieldsConfig to server until the next step
      this.$store.commit('fieldsConfig/CLONE_TEMPLATE', {
        widget: this.factoryName,
        from: templateName,
        to: newName
      });
      return newName;
    },
    //these methods are also in positions.js
    calculateDailyPercent(daily, mktVal) {
      let ldm = mktVal - daily;
      return ldm !== 0 ? (daily * 100) / Math.abs(ldm) : NaN;
    },
    calculateUnrealizedPLPercent(unrealizedPL, bookVal) {
      let bookValueAbs = Math.abs(bookVal);
      return bookValueAbs !== 0 ? (unrealizedPL * 100) / bookValueAbs : NaN;
    },
    //EOR
    onCsvExport() {
      this.gridOptions.api.exportDataAsCsv({
        fileName: this.factoryName + '.export.csv',
        columnKeys: this.gridOptions.columnApi.getAllDisplayedColumns().reduce((acc, col) => {
          if (!this.excludedCsvExportFids.includes(col.colDef.field)) {
            acc.push(col.colId);
          }
          return acc;
        }, []),
        skipRowGroups: true,
        processCellCallback: params => {
          if (params.value === undefined || params.value === null || params.value.toString() === 'NaN') return '';
          if (params.value instanceof Date) return params.value.toLocaleDateString('en-US');

          let momentDate = moment(params.value, 'YYYY-MM-DDTHH:mm:ssZZ', true);
          if (momentDate.isValid()) {
            return momentDate.format('MM/DD/YYYY hh:mm A');
          } else {
            momentDate = moment(params.value, 'DD MMM YYYY', true);
            if (momentDate.isValid()) {
              return momentDate.format('MM/DD/YYYY');
            }
          }
          if (params.column.colId === 'dailyPLPercent') {
            let value = this.calculateDailyPercent(params.node.data.dailyPL, params.node.data.marketValue);
            return this.$f(value, 'float.percentage.change');
          }
          if (params.column.colId === 'unrealizedPLPercent') {
            let value = this.calculateUnrealizedPLPercent(params.node.data.unrealizedPL, params.node.data.bookValue);
            return this.$f(value, 'float.percentage.change');
          }
          return params.value.toString();
        }
      });
    },
    onSecuritiesTypeChange(val) {
      //fetch is removed from here as other controls affected by this are doing that, and this is causing double fetch
      this.gridOptions.maintainColumnOrder = true;
      this.$store.commit(`widget/${this.wid}/SECURITIES_TYPE_CHANGED`, val);
    },
    onTableReady(ev, ariaLabel) {
      this.setRowsHeight();
      this.setColumnState(ev.columnApi);
      this.setHeaderRowObservers();
      this.loadTableSort();
      this.gridOptions.api.setGridAriaProperty('label', ariaLabel);
    },
    onTableSort({ columnApi }) {
      this.$store.commit(`widget/${this.wid}/TABLE_SORT_CHANGED`, this.getSortState(columnApi));
    },
    setColumnState(colApi = this.gridOptions.columnApi) {
      if (this.isSidebar) {
        if (this.columnState && this.columnState.length) {
          colApi.applyColumnState({
            state: this.columnState
          });
        }
      } else if (this.templateColumnState && this.templateColumnState.length) {
        colApi.applyColumnState({
          state: this.templateColumnState
        });
      }
    },
    setSortState(state, columnApi = this.gridOptions.columnApi) {
      columnApi.applyColumnState({ state, defaultState: { sort: null } });
    },
    loadTableSort() {
      let tableSort = this.widgetStore.tableSort;

      if (typeof this.controlAutosort === 'boolean') {
        this.defaultColumnDef.sortable = this.controlAutosort;
        this.setColumnDefs();

        if (this.controlAutosort) {
          if (!this.customTickerSort && tableSort.length && tableSort[0].colId === 'q._TCR') {
            this.gridOptions.api.setSuppressRowDrag(true);
          }
        } else {
          let sortState = this.getSortState();
          if (sortState.length || typeof this.customTickerSort !== 'boolean') {
            tableSort = [];
          } else {
            tableSort = [{ colId: 'q._TCR', sort: 'asc', sortIndex: 0 }];
          }
        }
      }
      this.setSortState(tableSort);
    },
    sortComparator(value1, value2) {
      if (this.controlAutosort === false) return null;

      const val1 = !value1 && value1 != 0 ? Infinity : value1;
      const val2 = !value2 && value2 != 0 ? Infinity : value2;

      if (!isNaN(val1) && !isNaN(val2)) {
        return val1 - val2;
      } else if (
        moment(val1, ['DD MMM YYYY', 'YYYY-MM-DDTHH:mm:ss'], true).isValid() &&
        moment(val2, ['DD MMM YYYY', 'YYYY-MM-DDTHH:mm:ss'], true).isValid()
      ) {
        return moment(val1) - moment(val2);
      } else {
        // move the '-' to the end when sorting
        if (val1.toString() === '-') return 1;
        if (val2.toString() === '-') return -1;
        const v1 = val1.toString() !== 'NaN' ? val1.toString() : '-';
        const v2 = val2.toString() !== 'NaN' ? val2.toString() : '-';
        return v1.localeCompare(v2);
      }
    },
    clearPinned() {
      this.gridOptions.columnApi.applyColumnState({ defaultState: { pinned: null } });
    },
    streamingConnect(args) {
      return new Promise((resolve, reject) => {
        if (!API.Streaming.isConnected) {
          API.Streaming.connect({
            timeout: 10000,
            monitor: {
              interval: 10000,
              timeout: 5000
            },
            visibilityMonitorEnabled: true
          }).then(function() {
            resolve(args);
          }, reject);
        } else {
          resolve(args);
        }
      });
    },
    streamingSubscribe(rics, gridAPI) {
      if (!rics || !rics.length) return;
      return this.streamingConnect()
        .then(() => {
          const subscriptionId = API.Streaming.Quote.subscribe(
            {
              rics,
              fids: streamableFidsFilter(this.requestFids)
            },
            // Subscribe Callback
            (_response, error) => error && console.error('Streaming subscription', error),
            // Snapshot Callback
            (response, error) => this.handleSnapshot(response, error, gridAPI),
            // Update Callback
            (response, error) => {
              this.handleUpdate(response, error, gridAPI);
            }
          );
          this.subscriptionIds.push(subscriptionId);
          return subscriptionId;
        })
        .then(() => this.ulStreamingSubscription(rics));
    },
    //To subscribe streaming to underlying RICs of Options
    async ulStreamingSubscription(rics, isAddRic = false, callback) {
      if (!rics || !rics.length) return;
      if (!isAddRic && this.underlyingStreamingId) {
        this.streamingUnsubscribe(this.underlyingStreamingId);
        this.underlyingStreamingId = null;
      }

      const column = this.gridOptions.columnApi.getColumn('calc.ITM_OTM');
      if (!column) return;

      rics = Array.from(new Set(rics));
      let response = await API.Rest.get(`/quote/info?rics=${rics}&fids=q.RIC,q._TYPE,q.STOCK_RIC`);
      response = response.data.data;
      response.forEach(e => {
        if (e['q._TYPE'] === 'OPT') {
          const { optionType } = this.getOptionDetails(e['q.RIC']);
          this.optionTypeCache[e['q.RIC']] = optionType === 'call' ? 'C' : 'P';
          if (!this.underlyingRicsMap[e['q.STOCK_RIC']]) this.underlyingRicsMap[e['q.STOCK_RIC']] = [];
          this.underlyingRicsMap[e['q.STOCK_RIC']].push(e['q.RIC']);
        }
      });
      if (Object.keys(this.underlyingRicsMap).length) {
        //Underyling price Streaming
        const underlyingRics = Object.keys(this.underlyingRicsMap);

        if (isAddRic && this.underlyingStreamingId) {
          API.Streaming.Quote.addRics(this.underlyingStreamingId, underlyingRics);
        } else {
          this.streamingConnect().then(() => {
            this.underlyingStreamingId = API.Streaming.Quote.subscribe(
              {
                rics: underlyingRics,
                fids: ['q._TRDPRC_1']
              },
              // Subscribe Callback
              (_response, error) => error && console.error('Streaming subscription', error),
              // Snapshot Callback
              (response, error) => handleUpdate(response, error),
              // Update Callback
              (response, error) => {
                handleUpdate(response, error);
              }
            );
            this.subscriptionIds.push(this.underlyingStreamingId);
            return this.underlyingStreamingId;
          });
        }

        const handleUpdate = (response, error) => {
          const ric = response.payload[0].ric;
          if (rics.includes(ric)) {
            callback ? callback(response.payload[0], error) : this.handleUpdate(response, error);
          }
          const underlyingTradePrice = response.payload[0].data['q._TRDPRC_1'];
          if (this.underlyingRicsMap[ric] && underlyingTradePrice) {
            this.underlyingRicsMap[ric].forEach(underlyingRic => {
              response.payload[0] = {
                ric: underlyingRic,
                data: {
                  'q.RIC': underlyingRic,
                  'calc.ITM_OTM': {
                    underlyingTradePrice
                  }
                }
              };

              callback ? callback(response.payload[0], error) : this.handleUpdate(response, error);
            });
          }
        };
      }
    },
    ulStreamingRemoveRic(ricsToRemove, allRics) {
      if (this.underlyingStreamingId) {
        if (Array.isArray(ricsToRemove)) ricsToRemove = [ricsToRemove];
        let underlyingRics = Object.keys(this.underlyingRicsMap).filter(ulRic => {
          if (!allRics.includes(ulRic)) {
            const index = this.underlyingRicsMap[ulRic].findIndex(r => ricsToRemove.includes(r));
            if (index >= 0) {
              this.underlyingRicsMap[ulRic].splice(index, 1);
              return this.underlyingRicsMap[ulRic].length === 0;
            }
          }
          return false;
        });

        if (underlyingRics.length > 0) {
          API.Streaming.Quote.removeRics(this.underlyingStreamingId, underlyingRics);
        }
      }
    },
    handleSnapshot(response, err, gridAPI) {
      if (err) return console.error('Streaming snapshot error', err);
      this.handleUpdate(response, err, gridAPI);
    },
    streamingUnsubscribe(id) {
      this.streamingConnect().then(() => {
        if (!this.subscriptionIds.length || (id && !this.subscriptionIds.includes(id))) return;
        let unsubIds = [];
        if (id) unsubIds.push(id);
        else unsubIds = [...this.subscriptionIds];
        unsubIds.forEach(id => {
          API.Streaming.Quote.unsubscribe(id);
          this.subscriptionIds.splice(this.subscriptionIds.indexOf(id), 1);
        });
      });
    },
    hydrateData(data, rowData = {}, options = {}) {
      if (data['q._PCTCHNG']) {
        this.$set(this.percentChanges, data['q.RIC'] || rowData['q.RIC'], +data['q._PCTCHNG']);
      }
      if (data['q._NETCHNG_1'] && options.updateNetChanges !== false) {
        this.$set(this.netChanges, data['q.RIC'] || rowData['q.RIC'], +data['q._NETCHNG_1']);
      }
      const outputData = {};
      this.tableFids.forEach(fid => {
        switch (fid) {
          case 'calc.52_WEEKS_RANGE':
            outputData[fid] = {
              start: data['d._LOW_1Y'] ? +data['d._LOW_1Y'] : rowData[fid].start,
              end: data['d._HIGH_1Y'] ? +data['d._HIGH_1Y'] : rowData[fid].end,
              mark: data['q._TRDPRC_1'] ? +data['q._TRDPRC_1'] : rowData[fid].mark
            };
            break;
          case 'calc.PERFORMANCE':
            outputData[fid] = +data['q._PCTCHNG'] || +rowData['q._PCTCHNG'];
            break;
          case 'calc.MARK':
            outputData[fid] = {
              bid: data['q._BID'] || rowData['q._BID'],
              ask: data['q._ASK'] || rowData['q._ASK']
            };
            break;
          case 'calc.PORTFOLIO_DAY_PROFIT_LOSS':
            outputData[fid] =
              (data['q._NETCHNG_1'] || rowData['q._NETCHNG_1']) *
              (data['calc.PORTFOLIO_ENTITY_QUANTITY'] || rowData['calc.PORTFOLIO_ENTITY_QUANTITY']);
            break;
          case 'calc.PORTFOLIO_DAY_PROFIT_LOSS_%': {
            const dailyPL =
              (data['q._NETCHNG_1'] || rowData['q._NETCHNG_1']) *
              (data['calc.PORTFOLIO_ENTITY_QUANTITY'] || rowData['calc.PORTFOLIO_ENTITY_QUANTITY']);
            const mktValue =
              parseFloat(data['q._TRDPRC_1'] || rowData['q._TRDPRC_1']) *
              (data['calc.PORTFOLIO_ENTITY_QUANTITY'] || rowData['calc.PORTFOLIO_ENTITY_QUANTITY']) *
              data['calc.PORTFOLIO_MULTIPLIER'];

            let ldm = mktValue - dailyPL;
            outputData[fid] = ldm !== 0 ? (dailyPL * 100) / Math.abs(ldm) : NaN;
            break;
          }
          case 'calc.PORTFOLIO_TOTAL_PROFIT_LOSS': {
            const mktValue =
              parseFloat(data['q._TRDPRC_1'] || rowData['q._TRDPRC_1']) *
              (data['calc.PORTFOLIO_ENTITY_QUANTITY'] || rowData['calc.PORTFOLIO_ENTITY_QUANTITY']) *
              data['calc.PORTFOLIO_MULTIPLIER'];
            const bookValue = data['calc.PORTFOLIO_COST_SUM'] || rowData['calc.PORTFOLIO_COST_SUM'];

            outputData[fid] = mktValue - bookValue;
            break;
          }
          case 'calc.PORTFOLIO_TOTAL_PROFIT_LOSS_%': {
            const mktValue =
              parseFloat(data['q._TRDPRC_1'] || rowData['q._TRDPRC_1']) *
              (data['calc.PORTFOLIO_ENTITY_QUANTITY'] || rowData['calc.PORTFOLIO_ENTITY_QUANTITY']) *
              data['calc.PORTFOLIO_MULTIPLIER'];
            const bookValue = data['calc.PORTFOLIO_COST_SUM'] || rowData['calc.PORTFOLIO_COST_SUM'];

            const totalPL = mktValue - bookValue;

            outputData[fid] = bookValue != 0 ? (totalPL * 100) / Math.abs(bookValue) : null;
            break;
          }
          case 'calc.PORTFOLIO_VALUE_SUM':
            outputData[fid] =
              parseFloat(data['q._TRDPRC_1'] || rowData['q._TRDPRC_1']) *
              (data['calc.PORTFOLIO_ENTITY_QUANTITY'] || rowData['calc.PORTFOLIO_ENTITY_QUANTITY']) *
              data['calc.PORTFOLIO_MULTIPLIER'];
            break;
          case 'calc.QUOTE_TICKER':
            outputData[fid] = (rowData[fid] || []).concat({
              bid: data['q._BID'] || rowData['q._BID'],
              ask: data['q._ASK'] || rowData['q._ASK']
            });
            break;
          case 'calc.RANGE':
            outputData[fid] = {
              high: data['q._HIGH_1'] ? data['q._HIGH_1'] : rowData[fid].high,
              low: data['q._LOW_1'] ? data['q._LOW_1'] : rowData[fid].low
            };
            break;
          case 'calc.REL_RANGE':
            outputData[fid] = {
              close: data['q._TRDPRC_1'] ? +data['q._TRDPRC_1'] : rowData[fid].close,
              open: data['q._OPEN_PRC'] ? +data['q._OPEN_PRC'] : rowData[fid].open,
              high: data['q._HIGH_1'] ? +data['q._HIGH_1'] : rowData[fid].high,
              low: data['q._LOW_1'] ? +data['q._LOW_1'] : rowData[fid].low
            };
            break;
          case 'calc.TREND':
            outputData[fid] = (rowData[fid] || []).concat(data['q._TRDPRC_1'] || rowData['q._TRDPRC_1']);
            break;
          case 'calc.TREND_ARROW':
            if (data['q._NETCHNG_1']) {
              outputData[fid] = +data['q._NETCHNG_1'] < 0 ? 'down' : +data['q._NETCHNG_1'] > 0 ? 'up' : 'right';
            } else outputData[fid] = rowData[fid] || 'right';
            break;
          case 'q.PRCTCK_1': {
            const charMap = {
              222: '+',
              254: '-'
            };
            outputData[fid] = data[fid] ? charMap[data[fid].charCodeAt(0)] : rowData[fid];
            break;
          }
          case 'q._TRDPRC_1':
            outputData[fid] = data[fid] || rowData[fid];
            outputData.change = data['q._NETCHNG_1'] || rowData.change;
            break;
          default:
            outputData[fid] = data[fid] || rowData[fid];
        }
      });
      // push unprocessed data to the output row
      Object.keys(rowData).forEach(fid => {
        if (!this.tableFids.includes(fid)) outputData[fid] = rowData[fid];
      });
      Object.keys(data).forEach(fid => {
        if (!this.tableFids.includes(fid)) outputData[fid] = data[fid];
      });
      return outputData;
    },
    setRowsHeight() {
      this.gridOptions.api.resetRowHeights();
      this.gridOptions.api.setHeaderHeight(this.agGridRowHeight);
    },
    updateLink(ev) {
      if (ev.node.isSelected()) {
        if (ev.node.group) {
          this.sendLinkUpdate(ev.node.key);
        } else {
          const ric = ev.data['q.RIC'] || ev.data['x._TICKER'];
          const ticker = ev.data['x._TICKER'];
          const country = ev.data['q._COUNTRY'];
          if (ticker && country) {
            fromSymbol(`${ticker}:${country}`).then(rics => {
              this.sendLinkUpdate(rics[0]);
            });
          } else {
            this.sendLinkUpdate(ric);
          }
        }
      }
    },
    tooltipValueGetter(fid) {
      return params => {
        let action = '';
        if (params.data) {
          if (params.data['q._TYPE'] === 'OPT') {
            if (fid === 'q._BID') {
              action = this.optionsBuyAtBid ? 'buy' : 'sell';
            } else if (fid === 'q._ASK') {
              action = this.optionsBuyAtBid ? 'sell' : 'buy';
            }
          } else {
            if (fid === 'q._BID') {
              action = this.stocksBuyAtBid ? 'buy' : 'sell';
            } else if (fid === 'q._ASK') {
              action = this.stocksBuyAtBid ? 'sell' : 'buy';
            }
          }
          if (
            (fid === 'q._BID' && isNaN(params.data['q._BID'])) ||
            (fid === 'q._ASK' && isNaN(params.data['q._ASK']))
          ) {
            return '';
          }
        }
        return action ? this.$t('white-label.widgets.orderEntry.actions.' + action) : '';
      };
    },
    onAsyncTransactionsFlushed() {
      /*  Enable to support widget synchronization during delayed updates
      if (this.reflushFlag.includes(this.wid)) {
        if (this.reflushFlag[0] !== this.wid) {
          this.$store.commit('widget/NO_SHARE_RESET_REFLUSH_FLAG', this.wid);
        }
      } else this.$store.commit('widget/NO_SHARE_SET_REFLUSH_FLAG', this.wid);
      */
    },
    openOrderEntry(params) {
      this.$widget.openOrderEntry(params);
    }
  },
  watch: {
    /* enable to support widget synchronization during delayed updates
    reflushFlag(newValue) {
      
      if (!newValue.includes(this.wid)) {
        this.gridOptions.api.flushAsyncTransactions();
      }
    },
    */
    commaSeparator() {
      this.gridOptions.api.redrawRows();
    },
    applicationLanguage() {
      this.gridOptions.api.redrawRows();
    },
    controlAutosort: {
      handler() {
        if (this.gridOptions && this.gridOptions.api) {
          this.loadTableSort();
        }
      }
    },
    controlFieldsConfigName: {
      handler(val) {
        if (this.gridOptions) this.gridOptions.maintainColumnOrder = false;
        if (val) this.selectedTemplateConfig = this.relatedTemplates.find(fc => fc.name === val);
      },
      immediate: true
    },
    fontSize() {
      this.$nextTick(() => {
        this.setRowsHeight();
        this.autoSizeColumns();
      });
    },
    netChanges: {
      handler() {
        const vals = Object.values(this.netChanges);
        if (!vals.length) return [0, 0];
        let advances = 0;
        let declines = 0;
        vals.forEach(v => {
          if (v > 0) advances++;
          else if (v < 0) declines++;
        });
        this.performanceBar.totalAdvances = advances;
        this.performanceBar.totalDeclines = declines;
        this.performanceBar.totalNeutral = vals.length - (advances + declines);
        this.performanceBar.advances = (advances / vals.length) * 100;
        this.performanceBar.declines = (declines / vals.length) * 100;
      },
      deep: true
    },
    _tableFids: {
      handler(val) {
        if (this.disableTableFidsUpdate) {
          this.disableTableFidsUpdate = false;
        } else {
          this.tableFids = val;
        }
      },
      immediate: true
    },
    tableFids(newFids, prevFids) {
      if (String(newFids) !== String(prevFids)) {
        this.fetch();
      }
    },
    twoDecimalPlaces() {
      this.gridOptions.api.redrawRows();
    },
    pinColumnsAllowed(value) {
      if (value === false) {
        this.clearPinned();
      }
    },
    stocksBuyAtBid() {
      if (this.gridOptions && this.gridOptions.api)
        this.gridOptions.api.refreshCells({ columns: ['q._BID', 'q._ASK'], force: true });
    },
    optionsBuyAtBid() {
      if (this.gridOptions && this.gridOptions.api)
        this.gridOptions.api.refreshCells({ columns: ['q._BID', 'q._ASK'], force: true });
    }
    //To make width change reactive
    // templateColumnState(newState, oldState) {
    //   console.log('Column state watcher:', newState);
    //   if (!this._.isEqual(newState, oldState)) {
    //     console.log('Applying col state:', this.wid);
    //     this.setColumnState();
    //   }
    // }
  },
  created() {
    this.onColResized = this._.debounce(this.onColResized, 250);
  }
};
